import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken,
  getIdTokenResult,
} from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  get,
  update,
  remove,
  push,
  query,
  orderByChild,
  equalTo,
} from "firebase/database";
import { FIREBASE_CONFIG } from "../utils/constants";

const app = initializeApp(FIREBASE_CONFIG);
const auth = getAuth(app);
const db = getDatabase(app);

export { app, auth, db };

/**
 * Firebase Authentication Service
 */
export const authService = {
  loginWithEmail: async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const token = await getIdToken(userCredential.user);
    const tokenResult = await getIdTokenResult(userCredential.user);
    return {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      token,
      refreshToken: userCredential.user.refreshToken,
      role: tokenResult.claims.role || "employee",
    };
  },

  registerWithEmail: async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const token = await getIdToken(userCredential.user);
    return {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      token,
      refreshToken: userCredential.user.refreshToken,
    };
  },

  logout: async () => {
    await firebaseSignOut(auth);
  },

  getIdToken: async (user) => {
    return getIdToken(user);
  },

  getIdTokenResult: async (user) => {
    return getIdTokenResult(user);
  },

  getUserDoc: async (uid) => {
    const snapshot = await get(ref(db, `users/${uid}`));
    if (!snapshot.exists()) return null;
    return { id: snapshot.key, ...snapshot.val() };
  },

  onAuthStateChanged: (callback) => {
    return onAuthStateChanged(auth, callback);
  },
};

/**
 * Firebase Realtime Database Service
 */
export const databaseService = {
  create: async (path, data) => {
    const refSnapshot = push(ref(db, path));
    await set(refSnapshot, { ...data, id: refSnapshot.key });
    return { id: refSnapshot.key, ...data };
  },

  read: async (path) => {
    const snapshot = await get(ref(db, path));
    if (!snapshot.exists()) return null;
    return { id: snapshot.key, ...snapshot.val() };
  },

  readAll: async (path) => {
    const snapshot = await get(ref(db, path));
    if (!snapshot.exists()) return [];
    return Object.entries(snapshot.val()).map(([key, value]) => ({ id: key, ...value }));
  },

  update: async (path, data) => {
    await update(ref(db, path), data);
    return true;
  },

  set: async (path, data) => {
    await set(ref(db, path), data);
    return { id: path.split("/").pop(), ...data };
  },

  delete: async (path) => {
    await remove(ref(db, path));
    return true;
  },

  queryByField: async (path, field, value) => {
    const q = query(ref(db, path), orderByChild(field), equalTo(value));
    const snapshot = await get(q);
    if (!snapshot.exists()) return [];
    return Object.entries(snapshot.val()).map(([key, val]) => ({ id: key, ...val }));
  },
};
